import classNames from "classnames";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { SmallCaretDownSolid } from "components/icons/SmallCaretDownSolid";
import {
  MONTH_LABELS,
  TIMELINE_AXIS_ELAPSED_COLOR,
  TIMELINE_EMPTY_SUBTEXT,
} from "constants/filingsTimeline";
import { useElementWidth } from "hooks/useElementWidth";
import { TimelineFilingRow } from "store/apis/filingsTimeline";
import { getSweepDelay } from "utils/filingsTimeline/getSweepDelay";
import { getTrackPosition } from "utils/filingsTimeline/getTrackPosition";
import { getYearFraction } from "utils/filingsTimeline/getYearFraction";
import { groupFilingsByProximity } from "utils/filingsTimeline/groupFilingsByProximity";
import { isTodayOverlappingDate } from "utils/filingsTimeline/isTodayOverlappingDate";
import { TimelineEmptyState } from "./TimelineEmptyState";
import { TimelineMarker } from "./TimelineMarker";
import { TimelineOverdueChip } from "./TimelineOverdueChip";

type TimelineTrackProps = {
  filings: TimelineFilingRow[];
  year: number;
  today: string;
  hasAnyFiling: boolean;
  overdueBefore: number;
  overdueAfter: number;
  onJumpToOverdue: (side: "left" | "right") => void;
  onChangeYear: (year: number) => void;
};

const FULL_PERCENT = 100;

export const TimelineTrack = ({
  filings,
  year,
  today,
  hasAnyFiling,
  overdueBefore,
  overdueAfter,
  onJumpToOverdue,
  onChangeYear,
}: TimelineTrackProps) => {
  const todayYear = dayjs(today).year();
  const hasToday = Number.isFinite(todayYear);
  const isCurrentYear = todayYear === year;
  const todayFraction = getYearFraction(today, year);

  const [isTodayShown, setIsTodayShown] = useState(false);
  const [trackRef, trackWidth] = useElementWidth<HTMLDivElement>();

  useEffect(() => {
    setIsTodayShown(false);

    const todayTimer = setTimeout(
      () => setIsTodayShown(true),
      getSweepDelay(todayFraction)
    );

    return () => clearTimeout(todayTimer);
  }, [todayFraction]);

  let elapsedPercent = 0;
  if (isCurrentYear) elapsedPercent = todayFraction * FULL_PERCENT;
  if (todayYear > year) elapsedPercent = FULL_PERCENT;

  const groups = groupFilingsByProximity(filings, year);

  const isTodayPushedDown = isTodayOverlappingDate(
    groups,
    todayFraction,
    trackWidth
  );

  const handleJumpLeft = () => onJumpToOverdue("left");
  const handleJumpRight = () => onJumpToOverdue("right");
  const goToCurrentYear = () => onChangeYear(todayYear);

  if (filings.length === 0) {
    return (
      <TimelineEmptyState
        subtext={hasAnyFiling ? undefined : TIMELINE_EMPTY_SUBTEXT}
        jumpYear={
          hasAnyFiling && hasToday && !isCurrentYear ? todayYear : undefined
        }
        onJumpToYear={goToCurrentYear}
      />
    );
  }

  return (
    <div ref={trackRef} className="t-relative t-h-[170px] t-min-w-[560px]">
      <div className="t-absolute t-bottom-[73px] t-left-6 t-right-6 t-h-0.5 t-animate-axis-wipe t-rounded-sm t-bg-neutral-10 motion-reduce:t-animate-none">
        <div
          className="t-h-full t-rounded-sm"
          style={{
            width: `${elapsedPercent}%`,
            backgroundColor: TIMELINE_AXIS_ELAPSED_COLOR,
          }}
        />
      </div>

      {isCurrentYear && hasToday && (
        <div
          className={classNames(
            "t-absolute t-inset-y-0 t-transition-opacity t-duration-300 motion-reduce:t-opacity-100 motion-reduce:t-transition-none",
            isTodayShown ? "t-opacity-100" : "t-opacity-0"
          )}
          style={{ left: getTrackPosition(todayFraction) }}
        >
          <span
            className={classNames(
              "t-absolute t-flex -t-translate-x-1/2 t-rotate-180 t-text-blue-80",
              isTodayPushedDown ? "t-bottom-[38px]" : "t-bottom-[58px]"
            )}
          >
            <SmallCaretDownSolid />
          </span>
          <span
            className={classNames(
              "t-absolute -t-translate-x-1/2 t-whitespace-nowrap t-text-caption t-font-medium t-leading-[18px] t-text-blue-80",
              isTodayPushedDown ? "t-bottom-[20px]" : "t-bottom-[40px]"
            )}
          >
            Today
          </span>
          <span
            className="t-absolute t-bottom-[68px] t-h-3 t-w-0.5 -t-translate-x-1/2 t-rounded-full"
            style={{ backgroundColor: TIMELINE_AXIS_ELAPSED_COLOR }}
          />
        </div>
      )}

      {groups.map((group) => (
        <TimelineMarker key={group.filings[0].uuid} group={group} />
      ))}

      {overdueBefore > 0 && (
        <TimelineOverdueChip
          side="left"
          count={overdueBefore}
          onClick={handleJumpLeft}
        />
      )}

      {overdueAfter > 0 && (
        <TimelineOverdueChip
          side="right"
          count={overdueAfter}
          onClick={handleJumpRight}
        />
      )}

      <div className="t-absolute t-bottom-1 t-left-6 t-right-6 t-h-3">
        {MONTH_LABELS.map((month, index) => (
          <span
            key={month}
            className="t-absolute t-top-0 t-text-[8px] t-leading-3 t-text-neutral-20"
            style={{ left: `${(index / MONTH_LABELS.length) * FULL_PERCENT}%` }}
          >
            {month}
          </span>
        ))}
      </div>
    </div>
  );
};
